import React, { useEffect, useState } from "react";
import { connect, useSelector } from "react-redux";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faKey } from "@fortawesome/free-solid-svg-icons";
import ClinvarForm from "./ClinvarForm";
import ClinvarToken from "./components/ClinvarToken";
import Modal from "../../components/atoms/Modal";
import {
  getChromosomeCoordinatesClinvarNotation,
  getClinvarSubmissionById,
  getHgvsNotationClinvarNotation,
  getSubmissionRefresh,
  getUserClinvarApiKey,
  getUserClinvarApiKeyHash,
} from "../../state/selectors/ClinvarSubmissions.selectors";
import {
  getApiUrl,
  getUserAuthError,
  getVariantId,
} from "../../state/selectors/general.selectors";
import {
  ChromosomeCoordinatesType,
  ClinvarSubmissionType,
} from "./types/types";
import { EmptyState } from "../../components/atoms";
import HowToSubmitToClinVar from "./HowToSubmitToClinVar";
import isLoggedIn from "../../common/helpers/isUserLoggedIn";
import { setClinvarNotation } from "./setters/ClinvarSetters";
import Loader from "../../components/Loader";
import { httpRequest } from "../../services/http";

const ClinvarContainer = ({
  clinvarApiKey,
  clinvarTokenHashed,
  submissionErrors,
  submissionId,
  hgvs,
  chromosomeCoordinates,
  editMode,
  setOpen,
  userAuthError,
  clinvarSubmission,
  refreshState,
  variantId,
  apiUrl,
}) => {
  const [tokenModalOpen, setTokenModalOpen] = useState(false);
  const varsomeHost = useSelector((state) => state.varsomeHost);
  const isUserLoggedIn = isLoggedIn();
  const { classification } = clinvarSubmission || {};
  const [requestFinished, setRequestFinished] = useState(false);
  const closeTokenModalOnSuccess = () => {
    setTokenModalOpen(false);
  };

  useEffect(() => {
    if (!isUserLoggedIn || !clinvarApiKey) return;
    const url = `${apiUrl}/get-clinvar-notation/${variantId}`;
    httpRequest
      .get(url)
      .then((response) => {
        setClinvarNotation(response.data);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(setRequestFinished(true));
  }, [isUserLoggedIn, clinvarApiKey]);

  const prefilledHGVS =
    classification?.clinvarSubmission[0]?.variantSet?.variant[0]?.hgvs;
  const prefilledChrCoords =
    classification?.clinvarSubmission[0]?.variantSet?.variant[0]
      ?.chromosomeCoordinates;

  const getTokenButton = (text) => (
    <div className="tw-inline-flex tw-items-center tw-float-right">
      <HowToSubmitToClinVar className="tw-pr-2" />
      <button
        type="button"
        onClick={() => setTokenModalOpen(true)}
        className="btn btn-success"
      >
        <FontAwesomeIcon
          icon={faKey}
          fixedWidth={true}
          className="tw-mr-2 tw-text-white"
        />
        {text}
      </button>
    </div>
  );

  return (
    <div>
      {isUserLoggedIn && !clinvarApiKey && (
        <div className="tw-flex tw-flex-col">
          <div className="tw-flex tw-items-center tw-justify-evenly tw-mb-3">
            <div className="tw-inline-flex tw-items-center tw-pr-4">
              <div className="tw-text-3xl">ClinVar Submission</div>
            </div>
            <HowToSubmitToClinVar className="tw-pl-2" />
          </div>

          <EmptyState
            icon={faKey}
            text="No ClinVar API key found"
            description={
              <span
                className="tw-underline tw-cursor-pointer tw-text-primary"
                onClick={() => setTokenModalOpen(true)}
              >
                Please configure your ClinVar API key
              </span>
            }
          />
        </div>
      )}

      {isUserLoggedIn &&
        requestFinished &&
        clinvarApiKey &&
        getTokenButton("Update API key")}
      <Modal
        stopEventBubbling={true}
        alertOnClose={true}
        onRequestClose={() => setTokenModalOpen(false)}
        isOpen={tokenModalOpen}
        className="tw-w-1/3"
        ContentRender={() => (
          <ClinvarToken
            clinvarApiKey={clinvarApiKey}
            clinvarTokenHashed={clinvarTokenHashed}
            closeTokenModalOnSuccess={closeTokenModalOnSuccess}
            userAuthError={userAuthError}
          />
        )}
      />
      {clinvarApiKey && !requestFinished && <Loader />}
      {clinvarApiKey && requestFinished && (
        <ClinvarForm
          setOpen={setOpen}
          classification={classification}
          hgvs={!editMode ? hgvs : prefilledHGVS}
          chromosomeCoordinates={
            !editMode ? chromosomeCoordinates : prefilledChrCoords
          }
          submissionErrors={submissionErrors}
          submissionId={submissionId}
          status={clinvarSubmission?.status}
          statusCode={clinvarSubmission?.status_code}
          clinvarAccession={clinvarSubmission?.clinvar_accession}
          refreshState={refreshState}
          isEdited={clinvarSubmission?.is_edited}
        />
      )}
      {!isUserLoggedIn && (
        <div className="tw-p-2">
          To submit to ClinVar, please
          <a
            className="tw-pl-2"
            href={`${varsomeHost}/sign-in/?next=${window.location}`}
          >
            sign in
          </a>
        </div>
      )}
    </div>
  );
};

ClinvarContainer.propTypes = {
  setOpen: PropTypes.func.isRequired,
  clinvarApiKey: PropTypes.string,
  clinvarTokenHashed: PropTypes.string,
  hgvs: PropTypes.string,
  submissionId: PropTypes.string,
  clinvarSubmission: ClinvarSubmissionType,
  chromosomeCoordinates: ChromosomeCoordinatesType,
  editMode: PropTypes.bool,
  submissionErrors: PropTypes.arrayOf(PropTypes.string),
  userAuthError: PropTypes.string,
  refreshState: PropTypes.number,
  variantId: PropTypes.string,
  apiUrl: PropTypes.string,
};

ClinvarContainer.defaultProps = {
  clinvarApiKey: "",
  clinvarTokenHashed: "",
  hgvs: "",
  submissionId: "",
  clinvarSubmission: null,
  chromosomeCoordinates: null,
  editMode: false,
  submissionErrors: [],
  userAuthError: "",
  refreshState: null,
  variantId: "",
  apiUrl: "",
};

const mapStateToProps = (state, ownProps) => ({
  clinvarApiKey: getUserClinvarApiKey(state),
  clinvarTokenHashed: getUserClinvarApiKeyHash(state),
  // hgvs: getHgvsNotationForClinvarSubmission(state),
  hgvs: getHgvsNotationClinvarNotation(state),
  clinvarSubmission: getClinvarSubmissionById(state, ownProps),
  // chromosomeCoordinates: getChromosomeCoordinates(state),
  chromosomeCoordinates: getChromosomeCoordinatesClinvarNotation(state),
  userAuthError: getUserAuthError(state),
  refreshState: getSubmissionRefresh(state),
  apiUrl: getApiUrl(state),
  variantId: getVariantId(state),
});

export default connect(mapStateToProps)(ClinvarContainer);
